/**
 * Discovery sheet section config — icons/colors come from relationshipGuide.ts
 * so lucide imports cannot drift out of sync with section definitions.
 */

import { Swords } from 'lucide-react';
import type { FC } from 'react';
import { getRelationshipGuideEntry } from './relationshipGuide';

export interface DiscoverySheetCallbacks {
  onClusterClick?: () => void;
  onPackageClick?: () => void;
  onExclusiveClick?: () => void;
  onDependencyClick?: () => void;
  onPrerequisiteClick?: () => void;
  onAggregationClick?: () => void;
  onBreakdownClick?: () => void;
  onContainsClick?: () => void;
  onElaborationClick?: () => void;
  onCollectionClick?: () => void;
}

export interface DiscoverySection {
  id: string;
  label: string;
  bond: string;
  Icon: FC<{ className?: string }>;
  accentBorder: string;
  iconColor: string;
  iconColorDark: string;
  question: string;
  hint: string;
  primaryAction?: {
    label: string;
    callbackKey: keyof DiscoverySheetCallbacks;
  };
  secondaryAction?: {
    label: string;
    callbackKey: keyof DiscoverySheetCallbacks;
  };
  /** When true, show a "not yet modeled" note instead of action buttons */
  notYetModeled?: boolean;
}

/** Guide ids wired through relationshipGuide — covered by unit test. */
export const DISCOVERY_GUIDE_IDS = [
  'cluster',
  'sequence',
  'package',
  'composition',
  'contains',
  'elaboration',
  'collection',
] as const;

function guideStyle(guideId: string): Pick<
  DiscoverySection,
  'Icon' | 'accentBorder' | 'iconColor' | 'iconColorDark'
> {
  const entry = getRelationshipGuideEntry(guideId);
  if (!entry) {
    throw new Error(`[relationshipDiscoverySections] Missing relationship guide entry: ${guideId}`);
  }
  return {
    Icon: entry.Icon,
    accentBorder: entry.accentBorder,
    iconColor: entry.iconColor,
    iconColorDark: entry.iconColorDark,
  };
}

export const DISCOVERY_SECTIONS: DiscoverySection[] = [
  {
    id: 'cluster',
    label: 'Cluster',
    bond: 'Identity',
    ...guideStyle('cluster'),
    question: 'Does anything else in the backlog describe the same underlying need — just worded differently or submitted by someone else?',
    hint: 'Look for near-identical titles. A Cluster signals a deduplication decision: one item becomes canonical, the others are suppressed.',
    primaryAction: { label: 'Create cluster', callbackKey: 'onClusterClick' },
  },
  {
    id: 'sequence',
    label: 'Sequence & conditions',
    bond: 'Before this · Waiting on this',
    ...guideStyle('sequence'),
    question: 'Is there something that must be addressed before this item can proceed? Does this item unlock anything else in the backlog?',
    hint: 'One sequence family, shown relative to this item: what must be addressed before it, and what is waiting on it.',
    primaryAction: { label: 'Add sequence link', callbackKey: 'onDependencyClick' },
  },
  {
    id: 'package',
    label: 'Package',
    bond: 'Atomicity',
    ...guideStyle('package'),
    question: 'Is there other work that would be incomplete or meaningless if this item were done alone? Do these items need to ship together?',
    hint: "A Package is about atomicity — the items inside it are prioritized or selected as a unit. If they can ship independently, they're not a Package.",
    primaryAction: { label: 'Create package', callbackKey: 'onPackageClick' },
  },
  {
    id: 'composition',
    label: 'Composition',
    bond: 'Whole and parts',
    ...guideStyle('composition'),
    question: 'Is this item composed of other discrete pieces of work already in the backlog? Or does it decompose into sub-items that exist or should exist?',
    hint: 'Aggregation and Breakdown share one parent→child structure. The add flow preserves whether the assertion was authored from the parts or the whole.',
    primaryAction: { label: 'Add composition', callbackKey: 'onAggregationClick' },
  },
  {
    id: 'contains',
    label: 'Contains',
    bond: 'Reporting hierarchy',
    ...guideStyle('contains'),
    question: 'Should this item sit under a parent in your hierarchy — like an epic containing stories — without claiming structural decomposition?',
    hint: 'Contains is passive: for navigation and reporting only. Use Breakdown when the parent genuinely consists of these parts for prioritization.',
    primaryAction: { label: 'Add to parent', callbackKey: 'onContainsClick' },
  },
  {
    id: 'elaboration',
    label: 'Elaboration',
    bond: getRelationshipGuideEntry('elaboration')?.bond ?? 'Possibility spectrum',
    ...guideStyle('elaboration'),
    question: 'How will you know when you have what you want? Does this need have possibilities ranging from minimally sufficient to ideal?',
    hint: 'Map the continuum and typically choose one possibility. Solution Spectrum captures different sufficiency or investment levels; Solution Variant captures different approaches at a similar level.',
    primaryAction: { label: 'Add elaboration', callbackKey: 'onElaborationClick' },
  },
  {
    id: 'competing',
    label: 'Competing',
    bond: 'Mutual exclusion',
    Icon: Swords,
    accentBorder: 'border-l-rose-500',
    iconColor: 'text-[var(--coral)]',
    iconColorDark: 'text-[var(--coral)]',
    question: 'Is there another item that, if prioritized, would make this one unnecessary — or that draws from the same budget, team, or outcome such that doing both would be redundant?',
    hint: 'Different from Elaboration: no shared parent needed. These are peer-level items that compete for the same decision slot. Different from Cluster: they are genuinely distinct — just mutually exclusive in practice.',
    primaryAction: { label: 'Mark as competing', callbackKey: 'onExclusiveClick' },
  },
  {
    id: 'collection',
    label: 'Collection',
    bond: 'Curation',
    ...guideStyle('collection'),
    question: 'Is there an initiative, theme, stakeholder group, or reporting category this item should be filed under?',
    hint: 'Collections carry no enforcement weight. They are named sets for reference, reporting, or communication. An item can belong to many collections.',
    primaryAction: { label: 'Add to collection', callbackKey: 'onCollectionClick' },
  },
];
